import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import PageHeader, { KpiTile } from '../components/common/PageHeader';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { NativeSelect, TextArea } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage } from '../services/api';
import { ExpenseCategory, Frequency, RecurringExpense } from '../types';
import { fmtCurrency, fmtDate, PAYMENT_MODES, titleCase, todayISO, toNum } from '../utils/format';
import { compact, focusFirstError, hasErrors, serverFieldErrors, v } from '../utils/validation';
import { CategoryChip } from './ExpensesPage';

const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half-yearly' },
  { value: 'yearly', label: 'Yearly' },
];

interface FormState {
  name: string;
  categoryId: string;
  payee: string;
  amount: string;
  taxAmount: string;
  frequency: Frequency;
  paymentMode: string;
  startDate: string;
  nextDueDate: string;
  endDate: string;
  notes: string;
}

const emptyForm = (): FormState => ({
  name: '', categoryId: '', payee: '', amount: '', taxAmount: '', frequency: 'monthly',
  paymentMode: 'auto_debit', startDate: todayISO(), nextDueDate: '', endDate: '', notes: '',
});

interface PostResult {
  count: number;
  totalAmount: number;
  posted: { name: string; expenseDate: string; totalAmount: number }[];
}

export default function RecurringExpensesPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const canManage = hasPermission('expenses:manage');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<RecurringExpense | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const { data: categories = [] } = useQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => api.get<ExpenseCategory[]>('/expense-categories'),
  });
  const { data: items = [], isLoading } = useQuery({
    queryKey: queryKeys.recurring(),
    queryFn: () => api.get<RecurringExpense[]>('/recurring-expenses'),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.recurring() });
    queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? api.patch(`/recurring-expenses/${editing.id}`, payload) : api.post('/recurring-expenses', payload),
    onSuccess: invalidate,
  });
  const postMutation = useMutation({
    mutationFn: (ids?: number[]) => api.post<PostResult>('/recurring-expenses/post-due', { ids }),
    onSuccess: invalidate,
  });
  const toggleMutation = useMutation({
    mutationFn: (r: RecurringExpense) => api.patch(`/recurring-expenses/${r.id}`, { isActive: !r.isActive }),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/recurring-expenses/${id}`),
    onSuccess: invalidate,
  });

  const active = items.filter((r) => r.isActive);
  const due = active.filter((r) => r.isDue);
  const monthlyBurn = active.reduce((s, r) => s + r.monthlyCost, 0);

  function open(r?: RecurringExpense) {
    setEditing(r ?? null);
    setErrors({});
    setForm(
      r
        ? {
            name: r.name, categoryId: String(r.categoryId), payee: r.payee, amount: String(r.amount),
            taxAmount: r.taxAmount ? String(r.taxAmount) : '', frequency: r.frequency, paymentMode: r.paymentMode,
            startDate: r.startDate, nextDueDate: r.nextDueDate, endDate: r.endDate ?? '', notes: r.notes ?? '',
          }
        : emptyForm(),
    );
    setShowModal(true);
  }

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  function validate(): boolean {
    const anchor = editing ? form.nextDueDate : form.startDate;
    const e = compact({
      name: v.businessName(form.name, 'Name', true, 150),
      categoryId: form.categoryId ? undefined : 'Category is required',
      payee: v.businessName(form.payee, 'Vendor', true, 150),
      amount: v.money(form.amount, 'Amount'),
      taxAmount: v.money(form.taxAmount, 'GST', { required: false, allowZero: true }) ?? v.taxWithinLimit(form.amount, form.taxAmount),
      frequency: form.frequency ? undefined : 'Frequency is required',
      paymentMode: form.paymentMode ? undefined : 'Payment mode is required',
      startDate: editing ? undefined : v.date(form.startDate, 'First due date'),
      nextDueDate: editing
        ? v.date(form.nextDueDate, 'Next due date', { min: form.startDate, minLabel: `the first due date (${fmtDate(form.startDate)})` })
        : undefined,
      endDate: v.date(form.endDate, 'End date', { required: false, min: anchor || undefined, minLabel: editing ? 'the next due date' : 'the first due date' }),
      notes: v.text(form.notes, 'Notes', { max: 1000 }),
    });
    setErrors(e);
    if (hasErrors(e)) focusFirstError();
    return !hasErrors(e);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      categoryId: Number(form.categoryId),
      payee: form.payee.trim(),
      amount: toNum(form.amount),
      taxAmount: toNum(form.taxAmount) ?? 0,
      frequency: form.frequency,
      paymentMode: form.paymentMode,
      endDate: form.endDate || null,
      notes: form.notes.trim(),
    };
    if (editing) payload.nextDueDate = form.nextDueDate;
    else payload.startDate = form.startDate;
    try {
      await saveMutation.mutateAsync(payload);
      showToast(editing ? 'Recurring bill updated' : 'Recurring bill added', 'success');
      setShowModal(false);
    } catch (err) {
      setErrors(serverFieldErrors(err, Object.keys(form) as (keyof FormState)[]));
      showToast(errorMessage(err, 'Failed to save'), 'error');
    }
  }

  async function post(ids?: number[]) {
    const targets = ids ? due.filter((d) => ids.includes(d.id)) : due;
    const yes = await confirm({
      title: 'Post Due Bills',
      message: `Record ${targets.length} due bill(s) as expenses? Each one is added to the expense ledger on its due date and moves to its next cycle.`,
      confirmText: 'Post to Expenses',
      variant: 'primary',
    });
    if (!yes) return;
    try {
      const result = await postMutation.mutateAsync(ids);
      showToast(`${result.count} expense(s) posted — ${fmtCurrency(result.totalAmount)}`, 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to post'), 'error');
    }
  }

  async function remove(r: RecurringExpense) {
    const yes = await confirm({
      title: 'Delete Recurring Bill',
      message: `Delete "${r.name}"? Expenses already posted from it stay in the ledger.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!yes) return;
    try {
      await deleteMutation.mutateAsync(r.id);
      showToast('Recurring bill deleted', 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to delete'), 'error');
    }
  }

  const columns = [
    {
      key: 'name',
      label: 'Bill',
      render: (_: any, r: RecurringExpense) => (
        <div>
          <span className="text-sm font-semibold text-ink-900">{r.name}</span>
          <span className="block text-xs text-ink-500">{r.payee}</span>
        </div>
      ),
    },
    { key: 'category', label: 'Category', render: (_: any, r: RecurringExpense) => <CategoryChip category={r.category} /> },
    {
      key: 'totalAmount',
      label: 'Amount',
      render: (_: any, r: RecurringExpense) => (
        <div>
          <span className="text-sm font-semibold text-ink-900">{fmtCurrency(r.totalAmount)}</span>
          <span className="block text-[11px] text-ink-400">{titleCase(r.frequency)}{r.frequency !== 'monthly' ? ` · ${fmtCurrency(r.monthlyCost, 0)}/mo` : ''}</span>
        </div>
      ),
    },
    {
      key: 'nextDueDate',
      label: 'Next Due',
      render: (_: any, r: RecurringExpense) =>
        !r.isActive ? (
          <Badge variant="secondary" size="sm" rounded>Paused</Badge>
        ) : (
          <span className={`text-sm ${r.isDue ? 'text-red-600 font-semibold' : 'text-ink-700'}`}>
            {fmtDate(r.nextDueDate)}
            {r.isDue && <span className="block text-[10px] font-bold uppercase">Due now</span>}
          </span>
        ),
    },
    ...(canManage
      ? [{
          key: 'actions',
          label: 'Actions',
          width: '280px',
          render: (_: any, r: RecurringExpense) => (
            <div className="flex items-center gap-2 flex-wrap">
              {r.isDue && (
                <button type="button" className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 whitespace-nowrap"
                  disabled={postMutation.isPending} onClick={() => post([r.id])}>
                  <i className="fas fa-check mr-1" />Post
                </button>
              )}
              <button type="button" className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200" onClick={() => open(r)}>
                <i className="fas fa-pen mr-1" />Edit
              </button>
              <button type="button" className="px-3 py-1 bg-white border border-ink-200 text-ink-600 text-xs rounded hover:bg-ink-50"
                onClick={() => toggleMutation.mutate(r, { onError: (err) => showToast(errorMessage(err), 'error') })}>
                {r.isActive ? 'Pause' : 'Resume'}
              </button>
              <button type="button" className="px-3 py-1 bg-red-50 text-red-600 text-xs rounded hover:bg-red-100" onClick={() => remove(r)}>
                <i className="fas fa-trash-can" />
              </button>
            </div>
          ),
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Recurring Bills"
          subtitle="AI subscriptions, WiFi, rent, cloud — set once, post to expenses each cycle in one click"
          actions={
            canManage && (
              <>
                {due.length > 0 && (
                  <Button variant="warning" icon="fa-bolt" onClick={() => post()} disabled={postMutation.isPending}>
                    Post {due.length} Due
                  </Button>
                )}
                <Button variant="primary" icon="fa-plus" onClick={() => open()}>Add Recurring Bill</Button>
              </>
            )
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <KpiTile label="Monthly Fixed Cost" value={fmtCurrency(monthlyBurn, 0)} icon="fa-fire" tone="red" hint="All active bills, normalised per month" />
          <KpiTile label="Active Bills" value={active.length} icon="fa-rotate" tone="brand" />
          <KpiTile label="Due Now" value={due.length} icon="fa-bell" tone={due.length ? 'accent' : 'green'}
            hint={due.length ? fmtCurrency(due.reduce((s, r) => s + r.totalAmount, 0)) : 'All caught up'} />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-ink-200 overflow-visible lg:overflow-hidden">
          <div className="px-1 py-3 lg:p-0">
            <ResponsiveTable columns={columns} data={items} isLoading={isLoading} height="calc(100vh - 340px)"
              emptyMessage="No recurring bills yet — add your AI tools, internet, rent…" />
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editing ? 'Edit Recurring Bill' : 'Add Recurring Bill'}
          size="lg"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit as any} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Name" required value={form.name} maxLength={150} placeholder="e.g. Claude Team — 5 seats"
                onChange={(e) => setField('name', e.target.value)} error={errors.name} />
              <Input label="Vendor" required value={form.payee} maxLength={150} placeholder="e.g. Anthropic"
                onChange={(e) => setField('payee', e.target.value)} error={errors.payee} />
              <NativeSelect label="Category" required value={form.categoryId} onChange={(val) => setField('categoryId', val)}
                placeholder="Select category…" error={errors.categoryId}
                options={categories.filter((c) => c.isActive && !c.systemCode).map((c) => ({ value: c.id, label: c.name }))} />
              <NativeSelect label="Frequency" required value={form.frequency} error={errors.frequency}
                onChange={(val) => setField('frequency', val)} options={FREQUENCIES} />
              <Input label="Amount (₹, before GST)" required type="number" value={form.amount}
                onChange={(e) => { setField('amount', e.target.value); setErrors((p) => ({ ...p, taxAmount: undefined })); }} error={errors.amount} />
              <Input label="GST (₹)" type="number" value={form.taxAmount} error={errors.taxAmount}
                onChange={(e) => setField('taxAmount', e.target.value)} helperText="Max 28% of amount" />
              <NativeSelect label="Payment Mode" required value={form.paymentMode} error={errors.paymentMode}
                onChange={(val) => setField('paymentMode', val)} options={PAYMENT_MODES} />
              {editing ? (
                <Input label="Next Due Date" type="date" required value={form.nextDueDate}
                  onChange={(e) => setField('nextDueDate', e.target.value)} error={errors.nextDueDate} />
              ) : (
                <Input label="First Due Date" type="date" required value={form.startDate}
                  onChange={(e) => setField('startDate', e.target.value)} error={errors.startDate}
                  helperText="A past date creates the back-dated entries when you post" />
              )}
              <Input label="End Date (optional)" type="date" value={form.endDate}
                onChange={(e) => setField('endDate', e.target.value)} error={errors.endDate} />
            </div>
            <TextArea label="Notes" value={form.notes} onChange={(val) => setField('notes', val)} maxLength={1000} error={errors.notes} />
          </form>
        </Modal>

        <ConfirmDialog />
      </div>
    </Layout>
  );
}
